import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import ConfirmDialog from "components/ConfirmDialog";
import { useFiles, useFileCategories } from "hooks/crm/useFiles";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { SearchProvider, useSearch } from "context/SearchContext";
import { countActiveFilters, filterFieldsFor } from "utilities/listFilters";
import SearchPanel from "components/SearchPanel";
import RecordTable from "components/Records/RecordTable";
import ListSearchBar from "components/Records/ListSearchBar";
import ListPagination from "components/Records/ListPagination";
import FilePreview from "components/FilePreview";
import { labelFor } from "utilities/fileCategories";
import {
  Upload,
  Download,
  Trash2,
  Pencil,
  Check,
  X,
  Eye,
  FileText,
  FolderOpen,
  Filter,
} from "lucide-react";

// SOUND-661: the hardcoded 8-category list that used to live here is GONE. Prod
// holds 157 distinct category values, so those 8 could not reach 1,426 files —
// 41.7% — and the Move control could only move a file INTO one of the 8. The
// vocabulary is server-owned now (GET /files/categories), same rule as ticket
// request types. Do not reintroduce a list here.

function formatBytes(bytes) {
  if (!bytes) return "\u2014";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr) {
  if (!dateStr) return "\u2014";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}


// ─── Upload Modal ───────────────────────────────────────────────

function UploadModal({ isOpen, onClose, onUpload, categories = [] }) {
  const [stagedFiles, setStagedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const addFiles = useCallback((fileList) => {
    const newEntries = Array.from(fileList).map((file) => ({
      file,
      displayName: file.name.replace(/\.[^.]+$/, ""),
      category: "general",
    }));
    setStagedFiles((prev) => [...prev, ...newEntries]);
  }, []);

  const removeFile = (index) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const updateEntry = (index, field, value) => {
    setStagedFiles((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry))
    );
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const handleSubmit = async () => {
    if (stagedFiles.length === 0) return;
    setUploading(true);
    try {
      const files = stagedFiles.map((e) => e.file);
      const meta = stagedFiles.map((e) => ({
        displayName: e.displayName.trim() || e.file.name,
        category: e.category,
      }));
      await onUpload(files, "general", meta);
      setStagedFiles([]);
      onClose();
    } catch {
      // error handled in hook
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setStagedFiles([]);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm p-4 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      onKeyDown={(e) => e.key === "Escape" && handleClose()}
    >
      <div className="bg-card text-card-foreground border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold">Upload Files</h2>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="btn btn-ghost h-9 w-9 p-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              dragOver
                ? "border-primary bg-primary/5"
                : "border-border hover:border-muted-foreground"
            }`}
          >
            <Upload className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              Drop files here or <span className="text-primary underline">browse</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              PDF, PNG, JPG, GIF up to 10 MB
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.gif"
              onChange={(e) => {
                if (e.target.files?.length) addFiles(e.target.files);
                e.target.value = "";
              }}
              className="hidden"
            />
          </div>

          {/* Staged file list */}
          {stagedFiles.length > 0 && (
            <div className="space-y-3">
              {stagedFiles.map((entry, i) => (
                <div
                  key={i}
                  className="surface ring-1 ring-border rounded-lg p-3 space-y-2"
                >
                  <div className="flex items-start gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground mt-2 flex-shrink-0" />
                    <div className="flex-1 min-w-0 space-y-2">
                      {/* Original filename + size */}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground truncate">
                          {entry.file.name} ({formatBytes(entry.file.size)})
                        </span>
                        <button
                          onClick={() => removeFile(i)}
                          className="p-1 text-muted-foreground hover:text-destructive flex-shrink-0"
                          title="Remove"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {/* Display name */}
                      <input
                        type="text"
                        value={entry.displayName}
                        onChange={(e) => updateEntry(i, "displayName", e.target.value)}
                        placeholder="Display name"
                        className="input h-8 text-sm w-full"
                      />
                      {/* Category */}
                      <select
                        value={entry.category}
                        onChange={(e) => updateEntry(i, "category", e.target.value)}
                        className="input h-8 text-sm w-full"
                      >
                        {categories.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/40">
          <span className="text-xs text-muted-foreground">
            {stagedFiles.length} file{stagedFiles.length !== 1 ? "s" : ""} selected
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              disabled={uploading}
              className="btn btn-ghost"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={uploading || stagedFiles.length === 0}
              className="btn btn-primary"
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────

function isPreviewable(mimeType) {
  return mimeType?.startsWith("image/") || mimeType === "application/pdf";
}


// ─── Files Tab ──────────────────────────────────────────────────

const FIELDS = filterFieldsFor("companyFiles");

function FilesTabInner({ companyId }) {
  const auth = useAuth();
  const internal = isInternal(auth.user);
  const { filters, set } = useSearch();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState("-createdAt");
  const [filtersOpen, setFiltersOpen] = useState(false);

  // companyId is FORCED, not filtered — it comes from the record you are on, and
  // the scoped namespace has no companyId field for a user filter to collide
  // with. Same rule as MidsTab.
  const {
    files,
    pagination,
    loading,
    error,
    uploadFiles,
    deleteFile,
    renameFile,
    moveFile,
    downloadFile,
    downloadAll,
    fetchAllIds,
    getPreviewUrl,
    refetch,
  } = useFiles({ companyId, filters, page, limit, sort });

  // What EXISTS here, for the filter — so the filter can always reach anything
  // on screen. The upload picker wants the full vocabulary instead, because it
  // offers every choice rather than only the ones already used.
  const { categories: presentCategories } = useFileCategories({ companyId });
  const { categories: allCategories } = useFileCategories({ companyId, all: true });

  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      setPage(1);
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  const activeFilterCount = countActiveFilters(filters, FIELDS);

  const [showUpload, setShowUpload] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    description: "",
    onConfirm: () => {},
  });
  const closeConfirmDialog = useCallback(
    () => setConfirmDialog((c) => ({ ...c, open: false })),
    []
  );

  const handleDelete = (fileId) => {
    setConfirmDialog({
      open: true,
      title: "Delete File",
      description: "Delete this file? This cannot be undone.",
      confirmLabel: "Delete",
      variant: "destructive",
      onConfirm: async () => {
        closeConfirmDialog();
        await deleteFile(fileId);
      },
    });
  };

  const startEdit = (file) => {
    setEditingId(file.fileId);
    setEditName(file.displayName || file.fileName);
    setEditCategory(file.category || "general");
  };

  /**
   * Saves BOTH name and category, and only what actually changed.
   *
   * Two endpoints because that is what the API offers (`renameFile` sends
   * displayName, `moveFile` sends category — both are PUT /files/:id). Firing
   * them unconditionally would stamp updatedAt on every file someone merely
   * looked at, so each is skipped when its value is untouched.
   */
  const submitEdit = async (file) => {
    const name = editName.trim();
    const nameChanged = name && name !== (file.displayName || file.fileName);
    const categoryChanged = editCategory && editCategory !== file.category;

    if (nameChanged) await renameFile(file.fileId, name);
    if (categoryChanged) await moveFile(file.fileId, editCategory);
    // moveFile refetches; a rename patches in place. Refetch when only the name
    // changed so the row reflects the server rather than optimistic state.
    if (nameChanged && !categoryChanged) await refetch();

    setEditingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditCategory("");
  };

  const handlePreview = async (file) => {
    const signedUrl = await getPreviewUrl(file.fileId);
    if (signedUrl) setPreviewFile({ ...file, signedUrl });
  };

  // Downloads everything matching the filters, not the visible page — see
  // fetchAllIds. The button is disabled while the id fetch is in flight so a
  // second click cannot start a second zip.
  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      const ids = await fetchAllIds();
      if (ids.length) await downloadAll(ids);
    } finally {
      setDownloading(false);
    }
  };

  const handleNameClick = (file) => {
    if (isPreviewable(file.mimeType)) handlePreview(file);
    else downloadFile(file.fileId);
  };

  const canDelete = (file) =>
    internal ||
    ["super", "admin"].includes(auth.user?.role) ||
    file.uploadedBy?.userId === auth.user?.userId;

  // A parent company owns no files itself — these belong to its children
  // (SOUND-661). Derived from the DATA rather than the company type: any row
  // whose companyId differs from the record means this is an aggregated view.
  // Same test MidsTab uses, and it covers parent without the tab needing to know.
  const isAggregated = files.some((f) => f.companyId && f.companyId !== companyId);

  const columns = useMemo(
    () => [
      isAggregated && {
        key: "companyName",
        width: "16%",
        header: "Client",
        // No sortKey: companyName is resolved AFTER the paginated query, so
        // Mongo cannot order by it. A sort affordance here would do nothing.
        exportValue: (f) => f.companyName || "",
        cell: (f) => <div className="truncate">{f.companyName || "—"}</div>,
      },
      {
        key: "name",
        width: isAggregated ? "26%" : "38%",
        header: "Name",
        sortKey: "displayName",
        exportValue: (f) => f.displayName || f.fileName,
        cell: (f) =>
          editingId === f.fileId ? (
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitEdit(f);
                  if (e.key === "Escape") cancelEdit();
                }}
                onClick={(e) => e.stopPropagation()}
                className="input h-7 text-sm flex-1"
                autoFocus
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  submitEdit(f);
                }}
                className="p-1 text-success hover:opacity-80"
                title="Save"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  cancelEdit();
                }}
                className="p-1 text-muted-foreground hover:text-foreground"
                title="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <button
                onClick={() => handleNameClick(f)}
                className="truncate text-left hover:text-brand transition-colors"
                title={isPreviewable(f.mimeType) ? "Preview" : "Download"}
              >
                {f.displayName || f.fileName}
              </button>
            </div>
          ),
      },
      {
        key: "category",
        width: isAggregated ? "16%" : "20%",
        header: "Category",
        sortKey: "category",
        // Labelled from the server vocabulary. The stored value is exported, not
        // the label — an export is data, and a label cannot be filtered on.
        exportValue: (f) => f.category,
        cell: (f) =>
          editingId === f.fileId ? (
            // Category is EDITABLE. `moveFile` has existed in the hook since the
            // tab was written and was never wired to anything — the recurring
            // "API more capable than the UI" gap. It matters more now that the
            // category IS the document's role.
            <select
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="select h-7 text-xs w-full"
              aria-label="Category"
            >
              {allCategories.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          ) : (
            <span className="badge badge-muted text-xs">
              {labelFor(presentCategories, f.category)}
            </span>
          ),
      },
      {
        key: "size",
        width: "8%",
        header: "Size",
        sortKey: "size",
        exportValue: (f) => f.size,
        cell: (f) => <span className="text-muted-foreground">{formatBytes(f.size)}</span>,
      },
      {
        key: "createdAt",
        width: "12%",
        header: "Uploaded",
        sortKey: "createdAt",
        exportValue: (f) => f.createdAt,
        cell: (f) => <span className="text-muted-foreground">{formatDate(f.createdAt)}</span>,
      },
      {
        key: "uploadedBy",
        width: "12%",
        header: "Uploaded By",
        // No sortKey: uploadedBy is an OBJECT, and 2,477 of 3,421 files carry
        // none at all (the registration upload path never stamped one), so a
        // sort affordance here would promise an order the data cannot give.
        exportValue: (f) => f.uploadedBy?.displayName || f.uploadedBy?.email || "",
        cell: (f) => (
          <span className="text-muted-foreground truncate">
            {f.uploadedBy?.displayName || f.uploadedBy?.email || "—"}
          </span>
        ),
      },
      {
        key: "actions",
        width: "10%",
        header: "",
        exportable: false,
        cell: (f) => (
          <div className="flex items-center justify-end gap-1">
            {isPreviewable(f.mimeType) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreview(f);
                }}
                className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                title="Preview"
              >
                <Eye className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                downloadFile(f.fileId);
              }}
              className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                startEdit(f);
              }}
              className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              title="Edit name and category"
            >
              <Pencil className="h-4 w-4" />
            </button>
            {canDelete(f) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(f.fileId);
                }}
                className="p-1.5 rounded hover:bg-muted transition-colors text-destructive"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ),
      },
    ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingId, editName, editCategory, presentCategories, allCategories, isAggregated, internal, auth.user]
  );

  return (
    <div className="space-y-4">
      <ListSearchBar
        value={filters.search || ""}
        onChange={(v) => set("search", v)}
        placeholder="Search file name"
      >
        <select
          className="select w-auto"
          value={filters.category || ""}
          onChange={(e) => set("category", e.target.value)}
          aria-label="Category"
        >
          <option value="">Category: Any</option>
          {presentCategories.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>

        <button className="btn btn-secondary" onClick={() => setFiltersOpen(true)}>
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="badge badge-brand ml-1">{activeFilterCount}</span>
          )}
        </button>

        {files.length > 0 && (
          <button
            className="btn btn-secondary"
            onClick={handleDownloadAll}
            disabled={downloading}
            title="Download every file matching the current filters"
          >
            <Download className="h-4 w-4" />
            {downloading ? "Preparing…" : "Download All"}
          </button>
        )}

        <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
          <Upload className="h-4 w-4" />
          Upload
        </button>
      </ListSearchBar>

      <RecordTable
        columns={columns}
        rows={files}
        rowKey={(f) => f.fileId}
        loading={loading}
        error={error}
        onRetry={refetch}
        filtered={activeFilterCount > 0 || Boolean(filters.search || filters.category)}
        sort={sort}
        onSortChange={(next) => {
          setSort(next);
          setPage(1);
        }}
        noMatch={{ icon: FolderOpen, heading: "No matching files" }}
        empty={{
          icon: FolderOpen,
          heading: "No files yet",
          description: "Upload documents to keep them with this client's record.",
          errorText: "Failed to load files.",
          action: (
            <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
              <Upload className="h-4 w-4" />
              Upload
            </button>
          ),
        }}
      />

      <ListPagination
        page={pagination.page || page}
        totalPages={pagination.totalPages || 0}
        total={pagination.total || 0}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={setLimit}
      />

      <UploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        onUpload={uploadFiles}
        categories={allCategories}
      />

      {previewFile && (
        <FilePreview
          file={previewFile}
          signedUrl={previewFile.signedUrl}
          onClose={() => setPreviewFile(null)}
        />
      )}

      <SearchPanel
        namespace="companyFiles"
        title="Filter Files"
        isExpanded={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        extraOptions={{ categoryOptions: presentCategories }}
      />

      <ConfirmDialog {...confirmDialog} onCancel={closeConfirmDialog} />
    </div>
  );
}

export default function FilesTab({ companyId }) {
  return (
    <SearchProvider namespace="companyFiles">
      <FilesTabInner companyId={companyId} />
    </SearchProvider>
  );
}
